/**
 * @fileoverview The three audiences Hans signs in, defined ONCE.
 *
 * Two surfaces read this list: the tabbed sign-in shell (AuthPortalLayout) and
 * the landing-page role chooser. Defining a role in one place is what stops the
 * two drifting — a role that gains a tab but no chooser card (or the reverse)
 * is a dead end for whichever audience it belongs to.
 *
 * The asymmetry worth knowing: an instructor and a student can BOTH sign
 * themselves in, so their chooser card points at their own sign-in tab — the
 * instructor's opened in Sign Up mode, because that card creates a studio. An
 * organization can create its own verified administrator account. The chooser
 * starts that setup; existing admins use the Organization sign-in tab.
 *
 * `loginPath` and `chooserTo` are different on purpose. `loginPath` is the
 * portal's own sign-in — the tab strip and "Already have an account?" send
 * people there — and it must open on Log In. `chooserTo` is the door for
 * somebody who picked a role on the landing page, which for a new instructor
 * is the sign-up form: asked for with `?signup=1`, never a separate path, so
 * the tab is still matched on `/login`.
 */

import { Music, GraduationCap, Building2 } from "lucide-react";
import { STUDENT_PORTAL_ENABLED } from "@/lib/featureFlags";

export const AUTH_PORTALS = [
  {
    id: "instructor",
    icon: Music,
    tab: "Instructor",
    loginPath: "/login",
    title: "Instructor Portal",
    subtitle: "Sign in to manage your studio, students, and lessons",
    chooserTitle: "I'm an instructor",
    chooserDesc: "Capture your lessons, review clear summaries, and keep students on track.",
    chooserTo: "/login?signup=1",
    chooserCta: "Create your studio",
  },
  {
    id: "student",
    icon: GraduationCap,
    tab: "Student",
    loginPath: "/student/login",
    title: "Student Portal",
    subtitle: "Sign in to view your lessons and track your practice",
    chooserTitle: "I'm a student",
    chooserDesc: "Read your lesson notes, log practice, and build a portfolio.",
    chooserTo: "/student/login",
    chooserCta: "Sign in with your invite",
  },
  {
    id: "organization",
    icon: Building2,
    tab: "Organization",
    loginPath: "/org/login",
    title: "Organization Portal",
    // Administrators and program viewers share this sign-in; a viewer lands on
    // the programs shared with them.
    subtitle: "Sign in to manage your organization, or to follow a program shared with you",
    chooserTitle: "We're an organization",
    chooserDesc: "A school, studio, or program with instructors of your own.",
    chooserTo: "/org/signup",
    chooserCta: "Create your organization",
  },
];

/** The portal a pathname belongs to, or undefined if it isn't a sign-in path. */
export function portalForPath(pathname) {
  return AUTH_PORTALS.find((p) => p.loginPath === pathname);
}

/**
 * Whether a portal is offered to visitors right now.
 *
 * Only the student portal is flagged (STUDENT_PORTAL_ENABLED). `active` is the
 * portal currently being viewed and is always kept: a student who follows a
 * link to /student/login on a build with the flag off must still see which tab
 * they are on, rather than a strip that highlights nothing.
 */
export function offeredPortals(active) {
  return AUTH_PORTALS.filter(
    (p) => p.id === active?.id || p.id !== "student" || STUDENT_PORTAL_ENABLED
  );
}
